import {
  ActionIcon,
  Button,
  Group,
  Select,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { IconPlus, IconRecycle, IconTrash } from '@tabler/icons-react';
import { i18nKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useSelector } from 'react-redux';
import { filter, isNonNullish, map, pipe } from 'remeda';
import type { SendingMedium } from 'shared/hooks/use-load-company-configuration';
import { SENDING_MEDIA, type SendingMedia } from 'types/sending-media';
import type { StoreState } from 'types/storeTypes';

export type SendingMediumSelectorProps = {
  mediums: Array<SendingMedium>;
  onCreateFirst: (name: SendingMedia) => void;
  onInsert: (medium: SendingMedium) => void;
  onRemove: (index: number) => void;
  onChangeName: (index: number, name: SendingMedia) => void;
  onChangeForceSending: (index: number, forceSending: boolean) => void;
};

export const SendingMediumSelector = ({
  mediums,
  onCreateFirst,
  onInsert,
  onRemove,
  onChangeName,
  onChangeForceSending,
}: SendingMediumSelectorProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { sending_medium: availableSendingMediumOptions } = useSelector(
    (state: StoreState) => state.app.constants,
  );

  const isEmpty = mediums.length === 0;
  const hasPrimaryMediumSelected = mediums.length > 0 && isNonNullish(mediums[0]?.name);
  const hasOnlyOneMedium = mediums.length === 1;
  const hasOptionalMediums = mediums.filter(medium => !medium.force_sending).length > 0;

  const getUsedMediumNames = (excludeIndex: number = -1) =>
    pipe(
      mediums,
      map((medium, index) => (index !== excludeIndex ? medium.name : null)),
      filter(Boolean),
    );

  const getAvailableOptions = (excludeIndex: number = -1) => {
    const usedNames = getUsedMediumNames(excludeIndex);

    return availableSendingMediumOptions.filter(option => {
      if (usedNames.includes(option.value)) return false;

      // Cannot have both 'post' and 'registered_post' simultaneously
      const hasPost = usedNames.includes('post');
      const hasRegisteredPost = usedNames.includes('registered_post');

      if (option.value === 'post' && hasRegisteredPost) return false;
      if (option.value === 'registered_post' && hasPost) return false;

      return true;
    });
  };

  const canAddAdditionalMedium = hasPrimaryMediumSelected && getAvailableOptions().length > 0;
  const canRemoveCurrentMedium = !hasOnlyOneMedium;

  const addNewMedium = () => {
    const availableOptions = getAvailableOptions();

    if (availableOptions.length > 0 && canAddAdditionalMedium) {
      onInsert({
        name: availableOptions[0].value,
        order: mediums.length + 1,
        force_sending: false,
      });
    }
  };

  const renderEmptySelect = () => (
    <Group align="end">
      <Select
        label={t(i18nKeys.DEBTOR.SENDING_MEDIUM)}
        placeholder={t(i18nKeys.FORM.CHOOSE_MEDIA)}
        data={SENDING_MEDIA.map(medium => ({
          value: medium,
          label: t(tKeysLoose.REMINDER_LIST.BADGE[medium.toUpperCase()]),
        }))}
        value={null}
        onChange={value => value && onCreateFirst(value as SendingMedia)}
        style={{ flex: 1 }}
      />
      <ActionIcon color="red" variant="light" disabled size="lg">
        <IconTrash size={16} />
      </ActionIcon>
    </Group>
  );

  const renderLogicSelect = (medium: SendingMedium, index: number) => (
    <Tooltip
      label={
        medium.force_sending
          ? t(i18nKeys.SENDING_MEDIUM_MANAGER.TOOLTIP.AND_DESCRIPTION)
          : t(i18nKeys.SENDING_MEDIUM_MANAGER.TOOLTIP.OR_DESCRIPTION)
      }
    >
      <Select
        label={t(i18nKeys.SENDING_MEDIUM_MANAGER.LOGIC_LABEL)}
        data={[
          { label: t(i18nKeys.AND), value: 'true' },
          { label: t(i18nKeys.OR), value: 'false' },
        ]}
        value={String(medium.force_sending)}
        onChange={value => onChangeForceSending(index, value === 'true')}
        w={100}
      />
    </Tooltip>
  );

  const renderMediumSelect = (medium: SendingMedium, index: number) => {
    const availableOptions = getAvailableOptions(index);
    const isSecondary = index > 0;

    return (
      <Group key={index} align="end">
        {isSecondary && renderLogicSelect(medium, index)}
        <Select
          label={`${t(i18nKeys.DEBTOR.SENDING_MEDIUM)} ${index + 1}`}
          placeholder={t(i18nKeys.FORM.CHOOSE_MEDIA)}
          data={availableOptions.map(option => ({
            label: option.description,
            value: option.value,
          }))}
          clearable={hasOnlyOneMedium}
          allowDeselect={hasOnlyOneMedium}
          value={medium.name}
          onChange={value => onChangeName(index, value as SendingMedia)}
          style={{ flex: 1 }}
        />

        <ActionIcon
          color="red"
          variant="light"
          onClick={() => onRemove(index)}
          disabled={!canRemoveCurrentMedium}
          size="lg"
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    );
  };

  return (
    <Stack>
      {isEmpty ? renderEmptySelect() : mediums.map(renderMediumSelect)}

      {hasOptionalMediums && (
        <Group gap="xs" align="flex-start" wrap="nowrap">
          <IconRecycle
            size={16}
            color={theme.colors.purple[5]}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Text size="xs" c="dimmed">
            {t(i18nKeys.SENDING_MEDIUM_MANAGER.FALLBACK_MEDIUM_EXPLANATION)}
          </Text>
        </Group>
      )}

      <Group>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="outline"
          onClick={addNewMedium}
          disabled={!canAddAdditionalMedium}
        >
          {t(i18nKeys.ACTIONS.ADD)}
        </Button>
      </Group>
    </Stack>
  );
};
